import React, { useState } from 'react';
import AbilityTooltip from './AbilityTooltip';

/**
 * Ability descriptions in all languages
 */
const ABILITY_DESCRIPTIONS = {
  // English
  'Raid': 'Attack the opponent to get the effect',
  'Recover': 'Attack anything to heal your general for the same amount',
  'Mend wounds': 'This unit heals completely at the beginning of your turn',
  'Penetrate': 'Deal excess damage to the opponent',
  'Blitz': 'This unit can attack the turn it has been deployed',
  'Counter Strike': 'Use this order for free when you get it after a barrier has been dismantled',
  'Special': 'Special ability',
  'Redeem': 'Get the effect when the unit dies',
  'Sniper': 'Instantly kill any unit without a shield',
  'Deploy': 'Get the effect when the unit is deployed',
  'Protect': 'This unit must be attacked first',
  'Shield': 'This unit can avoid damage once',
  'Fell': 'Kill a unit to get the effect',
  // Spanish translations
  'Incursión': 'Ataca al oponente para obtener el efecto',
  'Recuperar': 'Ataca cualquier cosa para curar a tu general por la misma cantidad',
  'Curar heridas': 'Esta unidad se cura completamente al comienzo de tu turno',
  'Penetrar': 'Inflige daño excesivo al oponente',
  // NOTE: 'Blitz' is the same word in Spanish, so we use the English entry above
  'Contraataque': 'Usa esta orden gratis cuando la obtengas después de que una barrera haya sido desmantelada',
  'Especial': 'Habilidad especial',
  'Redimir': 'Obtén el efecto cuando la unidad muere',
  'Francotirador': 'Mata instantáneamente cualquier unidad sin escudo',
  'Desplegar': 'Obtén el efecto cuando la unidad es desplegada',
  'Proteger': 'Esta unidad debe ser atacada primero',
  'Escudo': 'Esta unidad puede evitar el daño una vez',
  'Caer': 'Mata una unidad para obtener el efecto',
  // German translations
  'Überfall': 'Greife den Gegner an, um den Effekt zu erhalten',
  'Erholen': 'Greife alles an, um deinen General um den gleichen Betrag zu heilen',
  'Wunden heilen': 'Diese Einheit heilt sich zu Beginn deines Zuges vollständig',
  'Durchdringen': 'Füge dem Gegner überschüssigen Schaden zu',
  // NOTE: 'Blitz' is the same word in German, so we use the English entry above
  'Gegenangriff': 'Verwende diese Order kostenlos, wenn du sie erhältst, nachdem eine Barriere abgebaut wurde',
  'Gegenschlag': 'Verwende diese Order kostenlos, wenn du sie erhältst, nachdem eine Barriere abgebaut wurde',
  'Konterangriff': 'Verwende diese Order kostenlos, wenn du sie erhältst, nachdem eine Barriere abgebaut wurde',
  'Spezial': 'Spezialfähigkeit',
  'Einlösen': 'Erhalte den Effekt, wenn die Einheit stirbt',
  'Erlösen': 'Erhalte den Effekt, wenn die Einheit stirbt',
  'Erlösung': 'Erhalte den Effekt, wenn die Einheit stirbt',
  'Scharfschütze': 'Töte sofort jede Einheit ohne Schild',
  'Einsetzen': 'Erhalte den Effekt, wenn die Einheit eingesetzt wird',
  'Schützen': 'Diese Einheit muss zuerst angegriffen werden',
  'Schild': 'Diese Einheit kann einmal Schaden vermeiden',
  'Fallen': 'Töte eine Einheit, um den Effekt zu erhalten'
};

/**
 * Map ability names to their base English name for description lookup
 */
const ABILITY_NAME_MAP = {
  // English (base names)
  'Raid': 'Raid',
  'Recover': 'Recover',
  'Mend wounds': 'Mend wounds',
  'Penetrate': 'Penetrate',
  'Blitz': 'Blitz',
  'Counter Strike': 'Counter Strike',
  'Special': 'Special',
  'Redeem': 'Redeem',
  'Sniper': 'Sniper',
  'Deploy': 'Deploy',
  'Protect': 'Protect',
  'Shield': 'Shield',
  'Fell': 'Fell',
  // Spanish
  'Incursión': 'Raid',
  'Recuperar': 'Recover',
  'Curar heridas': 'Mend wounds',
  'Penetrar': 'Penetrate',
  'Contraataque': 'Counter Strike',
  'Especial': 'Special',
  'Redimir': 'Redeem',
  'Francotirador': 'Sniper',
  'Desplegar': 'Deploy',
  'Proteger': 'Protect',
  'Escudo': 'Shield',
  'Caer': 'Fell',
  // German
  'Überfall': 'Raid',
  'Erholen': 'Recover',
  'Wunden heilen': 'Mend wounds',
  'Durchdringen': 'Penetrate',
  'Gegenangriff': 'Counter Strike',
  'Gegenschlag': 'Counter Strike',
  'Konterangriff': 'Counter Strike',
  'Spezial': 'Special',
  'Einlösen': 'Redeem',
  'Erlösen': 'Redeem',
  'Erlösung': 'Redeem',
  'Scharfschütze': 'Sniper',
  'Einsetzen': 'Deploy',
  'Schützen': 'Protect',
  'Schild': 'Shield',
  'Fallen': 'Fell'
};

/**
 * List of ability names to highlight in unit descriptions
 * These are matched case-insensitively as whole words
 * Includes English, Spanish (es), and German (de) translations
 */
const ABILITY_NAMES = [
  // English
  'Raid',
  'Recover',
  'Mend wounds',
  'Penetrate',
  'Blitz',
  'Counter Strike',
  'Special',
  'Redeem',
  'Sniper',
  'Deploy',
  'Protect',
  'Shield',
  'Fell',
  // Spanish translations
  'Incursión',
  'Recuperar',
  'Curar heridas',
  'Penetrar',
  // NOTE: 'Blitz' is the same in Spanish (already in English list above)
  'Contraataque',
  'Especial',
  'Redimir',
  'Francotirador',
  'Desplegar',
  'Proteger',
  'Escudo',
  'Caer',
  // German translations
  'Überfall',
  'Erholen',
  'Wunden heilen',
  'Durchdringen',
  // NOTE: 'Blitz' is the same in German (already in English list above)
  'Gegenangriff',
  'Gegenschlag',
  'Konterangriff',
  'Spezial',
  'Einlösen',
  'Erlösen',
  'Erlösung',
  'Scharfschütze',
  'Einsetzen',
  'Schützen',
  'Schild',
  'Fallen'
];

/**
 * Highlights ability names in a description string
 * Returns JSX with highlighted ability names
 * 
 * @param {string} description - The description text to highlight
 * @returns {React.ReactNode} - JSX with highlighted ability names
 */
export function highlightAbilities(description) {
  if (!description || typeof description !== 'string') {
    return description;
  }

  // Sort abilities by length (longest first) to match "Counter Strike" before "Counter"
  const sortedAbilities = [...ABILITY_NAMES].sort((a, b) => b.length - a.length);
  
  // Create regex pattern for all abilities (case-insensitive, whole word)
  // Escape special regex characters and match whole words/phrases
  const pattern = sortedAbilities
    .map(ability => {
      // Escape special regex characters
      const escaped = ability.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // For multi-word abilities, use word boundaries around the whole phrase
      // For single-word abilities, use word boundaries
      if (ability.includes(' ')) {
        // Multi-word: match as phrase with word boundaries on both ends
        return `\\b${escaped}\\b`;
      } else {
        // Single-word: match as whole word
        return `\\b${escaped}\\b`;
      }
    })
    .join('|');
  
  const regex = new RegExp(`(${pattern})`, 'gi');
  
  // Split description by matches and create JSX elements
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex
  regex.lastIndex = 0;
  
  while ((match = regex.exec(description)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(description.substring(lastIndex, match.index));
    }
    
    // Get the matched ability name (case-insensitive match)
    const matchedText = match[0];
    
    // Only highlight if the first letter is uppercase (capitalized)
    const firstChar = matchedText.charAt(0);
    const isCapitalized = firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase();
    
    if (isCapitalized) {
      // Find the exact ability name from our list (case-insensitive)
      const matchedAbility = ABILITY_NAMES.find(ability => 
        ability.toLowerCase() === matchedText.toLowerCase()
      ) || matchedText;
      
      // Get description for this ability (in the same language as the matched name)
      const tooltipText = ABILITY_DESCRIPTIONS[matchedAbility] || '';
      
      // Add highlighted match with black transparent background and golden text
      // Use AbilityTooltip component for portal-based tooltips
      parts.push(
        <AbilityTooltip
          key={`highlight-${match.index}`}
          tooltipText={tooltipText}
        >
          <span
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)', // Black transparent background
              color: '#FFD700', // Golden text
              fontWeight: '600', // Slightly bolder but not too bold
              padding: '1px 3px',
              borderRadius: '2px',
              textShadow: '0 0 3px rgba(255, 215, 0, 0.5)', // Subtle golden glow
              letterSpacing: '0.2px', // Slight spacing for tech feel
              cursor: 'help', // Show help cursor on hover
            }}
          >
            {match[0]}
          </span>
        </AbilityTooltip>
      );
    } else {
      // Not capitalized, just add as plain text without highlighting
      parts.push(matchedText);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < description.length) {
    parts.push(description.substring(lastIndex));
  }
  
  // If no matches, return original description
  if (parts.length === 0) {
    return description;
  }
  
  return <>{parts}</>;
}

/**
 * Alternative version that returns a string with HTML (for non-React contexts)
 * Not recommended for React, but available if needed
 */
export function highlightAbilitiesAsString(description) {
  if (!description || typeof description !== 'string') {
    return description;
  }

  const sortedAbilities = [...ABILITY_NAMES].sort((a, b) => b.length - a.length);
  
  const pattern = sortedAbilities
    .map(ability => {
      const escaped = ability.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // For multi-word abilities, use word boundaries around the whole phrase
      if (ability.includes(' ')) {
        return `\\b${escaped}\\b`;
      } else {
        return `\\b${escaped}\\b`;
      }
    })
    .join('|');
  
  const regex = new RegExp(`(${pattern})`, 'gi');
  
  return description.replace(regex, (match) => {
    return `<span style="background-color: rgba(0, 0, 0, 0.6); color: #FFD700; font-weight: 600; padding: 1px 3px; border-radius: 2px; text-shadow: 0 0 3px rgba(255, 215, 0, 0.5); letter-spacing: 0.2px;">${match}</span>`;
  });
}

